import { Text } from '@react-spectrum/s2';
import CheckmarkCircle from '@react-spectrum/s2/icons/CheckmarkCircle';
import ChevronDown from '@react-spectrum/s2/icons/ChevronDown';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { useEffect, useState } from 'react';

import { formatThinkingDuration, toPastTense } from '@/utils/verbTense.ts';

import type { ReasoningStep } from '../../../../../bridge/types/messages.ts';

import { MessageRow } from './MessageRow.tsx';

// Earliest and latest step timestamps in epoch ms (NaN-safe). Returns null when
// no step carries a parseable timestamp, so callers can omit the time prefix.
const stepTimeRange = (
	steps: ReasoningStep[],
): { start: number; end: number } | null => {
	let start = Infinity;
	let end = -Infinity;
	for (const s of steps) {
		const t = s.timestamp ? Date.parse(s.timestamp) : NaN;
		if (Number.isNaN(t)) continue;
		if (t < start) start = t;
		if (t > end) end = t;
	}
	return start === Infinity ? null : { start, end };
};

// Italic body-sm gray prose, matching the tool-comment line so the disclosure
// header reads as the same kind of muted agent aside.
const headerTextStyle = style({
	font: 'body',
	color: 'gray-600',
	fontSize: 'body-sm',
	fontStyle: 'italic',
});
const chevronStyle = iconStyle({ size: 'XS', color: 'neutral' });
const statusCheckStyle = iconStyle({ size: 'XS', color: 'gray' });

// Active step: bold, full-contrast neutral so the in-flight line is the thing
// the eye lands on (no spinner — weight + contrast carry the "working" cue).
// Done steps: regular weight, pulled back to a lighter gray so they read as a
// settled record.
const activeStepStyle = style({
	color: 'neutral',
	fontSize: 'body-sm',
	fontWeight: 'bold',
});
const doneStepStyle = style({ color: 'gray-500', fontSize: 'body-sm' });

// Same spinner weight the single-step ReasoningStep row and ThinkingIndicator
// use, so the working affordance is identical everywhere it appears.
const Spinner = () => (
	<span className="flex h-3 w-3 items-center justify-center">
		<span
			className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-current
				border-t-transparent"
		/>
	</span>
);

export const ReasoningStepList = ({
	steps,
	isLatest = true,
}: {
	steps: ReasoningStep[];
	// Whether this is the chat's most recent reasoning group. Only the latest one
	// can be live — turns are sequential (one stream per chat), so an EARLIER
	// group with a step stuck `active` (abnormal stream end) must still read as
	// settled instead of spinning forever next to a newer, genuinely-live group.
	isLatest?: boolean;
}) => {
	// Default to expanded while a step is still running so the user watches the
	// work live, then collapse to the summary once everything settles. A manual
	// click latches and wins from then on. A reopened chat (all steps already
	// done) lands collapsed because isActive is false.
	const isActive = isLatest && steps.some((s) => s.status === 'active');
	const [manual, setManual] = useState<boolean | null>(null);
	const expanded = manual ?? isActive;
	const toggle = () => setManual((prev) => !(prev ?? isActive));

	// Live clock: while the agent is still thinking, re-render once a second so
	// the elapsed prefix ticks up (Claude-style). Frozen once everything settles.
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!isActive) return;
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, [isActive]);

	if (steps.length === 0) return null;

	const allDone = !isActive;

	// Elapsed thinking time from the first step to the last (or to "now" while
	// still active). Omitted when no step carries a timestamp.
	const range = stepTimeRange(steps);
	const elapsedSeconds = range
		? ((allDone ? range.end : Math.max(range.end, now)) - range.start) / 1000
		: null;
	const elapsedLabel =
		elapsedSeconds != null ? formatThinkingDuration(elapsedSeconds) : null;

	// Generic status, Claude-style: a time prefix + a short thinking message.
	// "Thought for 1m 6s" once done; "1m 6s · thinking…" (or just "Thinking…"
	// when we have no clock) while in flight.
	const headerLabel = allDone
		? elapsedLabel
			? `Thought for ${elapsedLabel}`
			: 'Thought for a moment'
		: elapsedLabel
			? `${elapsedLabel} · thinking…`
			: 'Thinking…';

	return (
		<MessageRow data-testid="reasoning-step-list">
			<div className="w-full">
				<button
					type="button"
					aria-expanded={expanded}
					aria-label={`Reasoning steps, ${headerLabel}, ${
						expanded ? 'expanded' : 'collapsed'
					}`}
					onClick={toggle}
					className="flex w-full items-center gap-2 border-none bg-transparent
						px-0 py-1 text-left"
				>
					<span
						className="flex h-5 w-5 shrink-0 items-center justify-center
							leading-none"
					>
						{allDone ? (
							<CheckmarkCircle styles={statusCheckStyle} />
						) : (
							<Spinner />
						)}
					</span>

					<span
						className={`flex min-w-0 items-center leading-none ${
							expanded ? 'break-words' : 'truncate'
						}`}
					>
						<Text styles={headerTextStyle}>{headerLabel}</Text>
					</span>

					<span className="flex shrink-0 items-center justify-center">
						{expanded ? (
							<ChevronDown styles={chevronStyle} />
						) : (
							<ChevronRight styles={chevronStyle} />
						)}
					</span>
				</button>

				{/* Grid 0fr→1fr clips the step list to height 0 when collapsed and
					animates it open, instead of snapping a magic max-height. */}
				<div className="reasoning-disclosure" data-expanded={expanded}>
					<div className="min-h-0 overflow-hidden">
						{/* Row spacing comes from gap, not per-row padding, so the gray
							rule starts at the first line's top and ends at the last
							line's bottom instead of overhanging it. */}
						<div
							className="mt-1 ml-[28px] flex flex-col gap-3 border-l-2
								border-gray-200 pl-3"
						>
							{steps.map((step) => {
								// A settled (non-latest) group never has a live row, even if
								// one step is stuck `active` from an abnormal stream end.
								const active = isActive && step.status === 'active';
								return (
									<div
										key={step.id}
										data-testid="reasoning-step"
										className={`flex items-start gap-1.5 leading-tight ${
											active ? activeStepStyle : doneStepStyle
										}`}
									>
										<span>
											{active ? step.content : toPastTense(step.content)}
										</span>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</MessageRow>
	);
};
